import { createFileRoute } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { CopyButton } from '@/components/ui/CopyButton';
import { BUILD_KENNUNG, kennungText } from '@/lib/buildKennung';
import type { BuildKennung } from '@/lib/buildKennung';
import { useSchemaStand } from '@/hooks/useSchemaStand';
import { MitarbeiterAbschnitt } from '@/components/einstellungen/MitarbeiterAbschnitt';
import { WegeAbschnitt } from '@/components/einstellungen/WegeAbschnitt';
import { ListenAbschnitt } from '@/components/einstellungen/ListenAbschnitt';
import { NutzungAbschnitt } from '@/components/einstellungen/NutzungAbschnitt';
import type { SchemaStand } from '@/hooks/useSchemaStand';

/**
 * Die Kennung des laufenden Standes (A29).
 *
 * SICHTBAR, ABER LEISE. Sie steht unten in den Einstellungen, nicht im
 * Arbeitsbereich: Es ist eine Auskunft für den Zweifelsfall, kein
 * täglicher Inhalt. Wer sie braucht, sucht sie — wer sie nicht braucht,
 * soll sie nicht jeden Tag lesen müssen.
 *
 * ANTIPPBAR ZUM KOPIEREN, weil sie in eine Fehlermeldung getippt wird
 * und ein Hash aus sieben Zeichen sich abzuschreiben lohnt, aber leicht
 * verrutscht. `CopyButton` gibt es längst — keine neue Abhängigkeit.
 */
export function BuildKennungZeile({ kennung = BUILD_KENNUNG }: { kennung?: BuildKennung }) {
  // Die Kennung als Eigenschaft mit Vorgabewert: So laesst sich der
  // schmutzige Fall pruefen, ohne dafuer einen Bau anzuwerfen — und die
  // Seite selbst ruft sie weiterhin ohne Argument.
  const text = kennungText(kennung);
  return (
    <div className="mt-10 border-t border-line pt-4">
      <p className="flex flex-wrap items-center gap-1 text-xs text-ink-soft">
        <span className="tabular-nums">{text}</span>
        <CopyButton value={text} label="Stand kopieren" />
      </p>
      {kennung.schmutzig && (
        <p className="mt-1 text-xs text-ink-soft">
          Der Hash beschreibt nicht genau, was ausgeliefert wurde: beim Bauen lagen
          Änderungen im Arbeitsbaum, die in keinem Commit stehen.
        </p>
      )}
    </div>
  );
}

/**
 * Der Migrationsstand, neben der Bau-Kennung (A31.2).
 *
 * WARUM HIER
 *   Die Kennung aus A29 sagt, welcher STAND laeuft. Hier kommt dazu, ob
 *   das SCHEMA dazu passt. Beides zusammen beantwortet die Frage, die
 *   der Betreiber am 28.08. nicht beantworten konnte: „Was ist auf dem
 *   Server eigentlich angekommen?"
 *
 * NICHT AN DER FARBE HAENGEND
 *   Der Fall „steht aus" traegt eigenen TEXT („eine steht aus"), ein
 *   eigenes SYMBOL (Warndreieck) und den Befehl daneben. Die Farbe kommt
 *   obendrauf. Coral (#993C1D) auf Cream (#FDFBF7) hat 6,73:1 — ueber
 *   4,5:1 fuer Text und ueber 3:1 fuer das Symbol. Der gewoehnliche Fall
 *   steht in Ink-soft (#5A5854) auf Cream: 6,87:1.
 */
export function SchemaZeile({ stand }: { stand: SchemaStand | null }) {
  if (stand === null) {
    // Keine Auskunft ist keine Entwarnung — aber auch kein Alarm. Ohne
    // Zahl waere jede Aussage geraten, also steht hier nichts.
    return null;
  }
  const { gesamt, angewandt, ausstehend } = stand;
  if (ausstehend < 1) {
    return (
      <p className="mt-1 text-xs text-ink-soft">
        <span className="tabular-nums">
          Schema: {angewandt} von {gesamt} Migrationen
        </span>
      </p>
    );
  }
  const BEFEHL = 'php api/cli/migrate.php';
  return (
    <div className="mt-2 flex flex-wrap items-start gap-1.5 text-xs text-coral">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <p className="flex flex-wrap items-center gap-1">
        <span className="tabular-nums">
          Schema: {angewandt} von {gesamt} —{' '}
          {ausstehend === 1 ? 'eine steht aus' : `${ausstehend} stehen aus`}
        </span>
        {/* Trennzeichen wie in der Kennung darueber: ohne es liefen
            im Bau „…eine steht aus" und der Befehl in einer Zeile
            ineinander (gemessen im laufenden Build). */}
        <span aria-hidden>·</span>
        <span className="font-mono">{BEFEHL}</span>
        <CopyButton value={BEFEHL} label="Befehl kopieren" />
      </p>
    </div>
  );
}

/** Holt den Stand und zeigt ihn. Getrennt, damit die Zeile ohne Netz prüfbar bleibt. */
export function SchemaAuskunft() {
  const { data } = useSchemaStand();
  return <SchemaZeile stand={data?.schema ?? null} />;
}

/**
 * Die Einstellungsseite (A36).
 *
 * OBEN, WAS MAN EINSTELLT — UNTEN, WAS MAN NACHSIEHT. Bau-Kennung (A29)
 * und Schema-Stand (A31) sind Auskunft für den Zweifelsfall, keine
 * Einstellung; sie bleiben deshalb am Ende stehen, wo sie niemanden
 * aufhalten.
 *
 * Die Reihenfolge folgt dem Arbeitstag: erst wer arbeitet, dann woher
 * die Bestellung kommt und woher der Titel, dann wie er hinausgeht und
 * wo er bis dahin liegt.
 *
 * Weitere Abschnitte (Zustände, Checklisten, Verlage) kommen einzeln
 * und hängen sich hier daneben — jeder als eigene Komponente, jeder mit
 * eigenem Speichern.
 *
 * ZWEI MECHANIKEN, EIN AUSSEHEN: Die Wege (A40, WegeAbschnitt) und die
 * Listen (A41, ListenAbschnitt) sind parallel entstanden und lösen
 * dieselbe Aufgabe zweimal. Am Bildschirm sieht man das nicht, im
 * Quelltext schon — sie zusammenzulegen ist ein eigenes Paket
 * (Fallstrick 15), und bis dahin steht diese Zeile hier als Merkposten.
 */
function EinstellungenSeite() {
  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <MitarbeiterAbschnitt />
      <WegeAbschnitt art="bestellwege" />
      <WegeAbschnitt art="beschaffungswege" />
      <ListenAbschnitt
        typ="versandarten"
        ankerId="einst-versandarten"
        titel="Versandarten"
        einzahl="Versandart"
        herkunft="Wie die Sendung hinausgeht. Neue lassen sich anlegen; gelöscht wird
          nichts — an jeder Versandart hängen Bestellungen und Sendungen. Wer eine nicht
          mehr braucht, nimmt sie aus der Auswahl."
      />
      <ListenAbschnitt
        typ="checklisten_punkte"
        ankerId="einst-checklisten-punkte"
        titel="Checklisten-Punkte"
        einzahl="Checklisten-Punkt"
        herkunft="Die Haken, die an einer Bestellung abzuarbeiten sind. Neue lassen sich
          anlegen; gelöscht wird nichts — ein stillgelegter Punkt verschwindet aus der
          Checkliste, seine gesetzten Haken bleiben an den alten Vorgängen stehen."
      />
      <NutzungAbschnitt />
      <BuildKennungZeile />
      <SchemaAuskunft />
    </div>
  );
}

export const Route = createFileRoute('/einstellungen')({
  component: EinstellungenSeite
});
